// components/templates/sections/Hero.tsx
'use client';
import React from 'react';
import { useDesign } from '@/context/DesignContext';

interface HeroProps {
  image?: string;
  stats?: Array<{ label: string; number: string }>;
  title1?: string;
  title2?: string;
  subtitle?: string;
  ctaPrimary?: string;
  ctaSecondary?: string;
  design?: any;
}

export default function Hero({
  image,
  stats,
  title1,
  title2,
  subtitle,
  ctaPrimary,
  ctaSecondary,
  design: propDesign,
}: HeroProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;

  if (!image && (!stats || stats.length === 0) && !title1 && !title2) return null;

  const primaryColor = design?.colors?.primary || '#C8A96E';
  const secondaryColor = design?.colors?.secondary || '#0F0F0F';
  const bgColor = design?.colors?.background || '#0A0A0A';
  const textColor = design?.colors?.text || '#F5F5F5';
  const textLightColor = design?.colors?.textLight || '#A3A3A3';
  const headingFont = design?.typography?.headingFont || "'Cormorant Garamond', serif";
  const bodyFont = design?.typography?.bodyFont || "'Inter', sans-serif";
  const btnPrimary = design?.buttons?.primary || { backgroundColor: 'transparent', borderColor: primaryColor, textColor: primaryColor, padding: '12px 28px' };
  const btnSecondary = design?.buttons?.secondary || { backgroundColor: 'transparent', borderColor: textLightColor, textColor: textLightColor, padding: '10px 24px' };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" style={{ background: bgColor }}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute rounded-full" style={{ width: 600, height: 600, background: `radial-gradient(circle, ${primaryColor}0F 0%, transparent 70%)`, top: "10%", right: "-10%" }} />
        <div className="absolute rounded-full" style={{ width: 400, height: 400, background: `radial-gradient(circle, ${primaryColor}0A 0%, transparent 70%)`, bottom: "5%", left: "5%" }} />
        <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(${primaryColor}08 1px, transparent 1px), linear-gradient(90deg, ${primaryColor}08 1px, transparent 1px)`, backgroundSize: "80px 80px" }} />
      </div>

      <div className="relative z-10 w-full px-8 md:px-16 pt-24 pb-16 grid md:grid-cols-2 gap-12 items-center">
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-4">
            <div className="h-px flex-1 max-w-16" style={{ background: primaryColor }} />
            <span className="text-xs tracking-widest uppercase font-light" style={{ color: primaryColor, fontFamily: bodyFont }}>SS 2026 Collection</span>
          </div>

          {title1 && <h1 className="font-serif text-7xl md:text-9xl font-light leading-none" style={{ fontFamily: headingFont, color: textColor }}>{title1}</h1>}
          {title2 && <h1 className="font-serif text-7xl md:text-9xl font-light leading-none italic" style={{ fontFamily: headingFont, color: primaryColor }}>{title2}</h1>}
          {subtitle && <p className="font-light text-base leading-relaxed max-w-xs tracking-wide" style={{ fontFamily: bodyFont, color: textLightColor }}>{subtitle}</p>}

          <div className="flex items-center gap-4 flex-wrap">
            {ctaPrimary && (
              <button
                className="text-xs uppercase tracking-widest font-medium transition-all"
                style={{
                  background: btnPrimary.backgroundColor,
                  border: btnPrimary.borderColor ? `1px solid ${btnPrimary.borderColor}` : 'none',
                  color: btnPrimary.textColor,
                  padding: btnPrimary.padding,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = btnPrimary.backgroundColor === 'transparent' ? primaryColor : btnPrimary.backgroundColor;
                  e.currentTarget.style.color = btnPrimary.backgroundColor === 'transparent' ? secondaryColor : btnPrimary.textColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = btnPrimary.backgroundColor;
                  e.currentTarget.style.color = btnPrimary.textColor;
                }}
              >
                {ctaPrimary}
              </button>
            )}
            {ctaSecondary && (
              <button className="text-xs uppercase tracking-widest font-light flex items-center gap-2 transition-colors" style={{ color: textLightColor }}>
                <span>{ctaSecondary}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="m5 12 14 0M15 6l6 6-6 6"/>
                </svg>
              </button>
            )}
          </div>

          {stats && stats.length > 0 && (
            <div className="flex gap-8 mt-4 pt-8 border-t" style={{ borderColor: `${primaryColor}26` }}>
              {stats.map((stat) => (
                <div key={stat.label}>
                  <div className="font-serif text-3xl font-light" style={{ fontFamily: headingFont, color: primaryColor }}>{stat.number}</div>
                  <div className="text-xs uppercase tracking-widest mt-1" style={{ fontFamily: bodyFont, color: textLightColor }}>{stat.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {image && (
          <div className="relative flex justify-center md:justify-end">
            <div className="relative">
              <div className="relative overflow-hidden" style={{ width: 340, height: 480, borderRadius: design?.effects?.borderRadius || '2px' }}>
                <img src={image} alt="Hero" className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${secondaryColor}80 0%, transparent 60%)` }} />
              </div>
              <div className="absolute bottom-6 left-6 text-xs uppercase tracking-widest font-light" style={{ color: primaryColor, fontFamily: bodyFont }}>SS — 2026</div>
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <div className="text-xs uppercase tracking-widest" style={{ color: `${textLightColor}80`, fontFamily: bodyFont }}>Scroll</div>
        <div className="w-px h-16 relative overflow-hidden" style={{ background: `${primaryColor}33` }}>
          <div className="absolute inset-x-0 top-0 h-8 animate-bounce" style={{ background: primaryColor }} />
        </div>
      </div>
    </section>
  );
}